import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedClient } from '@/services/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Users, 
  AlertCircle, 
  Lightbulb,
  ThumbsUp,
  MapPin,
  MessageSquare,
  Loader2
} from 'lucide-react';
import { PeerInsight } from './types';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

export function PeerInsights() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ['peer-insights'],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('network_questions')
        .select(`
          id,
          question_text,
          topic_category,
          answer_count,
          view_count,
          created_at,
          region,
          author:profiles!network_questions_author_id_fkey(full_name, village)
        `)
        .is('deleted_at', null)
        .eq('country_id', 'b5b4a485-769c-49ed-8d38-3c0b8104f9e5')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) return [];
      return (data || []).map((q: any) => ({
        id: q.id,
        type: (q.answer_count > 0 ? 'tip' : 'alert') as PeerInsight['type'],
        message: q.question_text || '',
        region: q.region || (Array.isArray(q.author) ? q.author[0]?.village : q.author?.village) || 'Community',
        helpfulCount: q.view_count || 0,
        date: new Date(q.created_at),
      }));
    },
    staleTime: 1000 * 60 * 2,
  });

  const getInsightIcon = (type: PeerInsight['type']) => {
    switch (type) {
      case 'alert':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'tip':
        return <Lightbulb className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
    }
  };

  const getInsightBgColor = (type: PeerInsight['type']) => {
    switch (type) {
      case 'alert':
        return 'bg-orange-500/10 border-orange-500/50';
      case 'tip':
        return 'bg-yellow-500/10 border-yellow-500/50';
      default:
        return 'bg-orange-500/10 border-orange-500/50';
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6" />
            {t('knowledge.peers.title', { defaultValue: 'Peer Insights' })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('knowledge.peers.subtitle', { defaultValue: 'Learn from your farming community' })}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <h3 className="font-semibold text-foreground mb-2">
              {t('knowledge.peers.empty', { defaultValue: 'No peer discussions yet' })}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Be the first to ask!
            </p>
            <Button onClick={() => navigate('/network')}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Ask the Community
            </Button>
          </div>
        ) : (
          <>
            {/* Insights List */}
            <div className="space-y-3">
              {questions.map((insight) => (
                <Card
                  key={insight.id}
                  className={cn("p-4", getInsightBgColor(insight.type))}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getInsightIcon(insight.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <Badge variant="outline" className="capitalize">
                          {insight.type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(insight.date, { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-foreground mb-2">
                        {insight.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {insight.region}
                        </div>
                        <Button variant="ghost" size="sm" className="h-8 gap-1">
                          <ThumbsUp className="h-3 w-3" />
                          <span className="text-xs">{insight.helpfulCount}</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Ask Community Button */}
            <Card className="p-4 border-primary/50 bg-primary/5">
              <h3 className="font-semibold text-foreground mb-2">
                {t('knowledge.peers.askCommunity', { defaultValue: 'Ask the Community' })}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Have a question? Get answers from fellow farmers.
              </p>
              <Button className="w-full" onClick={() => navigate('/network')}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Go to Network
              </Button>
            </Card>
          </>
        )}
      </div>
    </ScrollArea>
  );
}
